import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["status", "version", "fingerprint"]
  static values = { saved: String, error: String, saving: String, unsaved: String }

  connect() {
    this.pending = null
    this.dirty = false
    this.submitting = false
    this.navigating = false
  }

  disconnect() { clearTimeout(this.timer) }

  schedule() {
    this.dirty = true
    this.statusTarget.textContent = this.unsavedValue
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.save(), 600)
  }

  async persist(data) {
    const files = Array.from(this.element.querySelector('input[type="file"]')?.files || [])
    const removals = data.getAll("assessment[remove_photos][]")
    this.dispatch("saving", { detail: { files } })
    try {
      const response = await fetch(this.element.action, { method: "PATCH", body: data, headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" } })
      const result = await response.json()
      if (!response.ok) throw new Error(result.errors?.join(" · ") || this.errorValue)
      this.versionTarget.value = result.lock_version
      if (this.hasFingerprintTarget) this.fingerprintTarget.value = result.content_fingerprint
      this.dispatch("saved", { detail: { files, removals, html: result.photos_html || "" } })
      return result
    } finally { this.dispatch("finished") }
  }

  save() {
    clearTimeout(this.timer)
    if (this.pending) return this.pending
    this.pending = this.drain().finally(() => { this.pending = null })
    return this.pending
  }

  async drain() {
    while (this.dirty) {
      this.dirty = false
      this.statusTarget.textContent = this.savingValue
      try {
        await this.persist(new FormData(this.element))
      } catch (error) {
        this.dirty = true
        this.statusTarget.textContent = error.message || this.errorValue
        return false
      }
    }
    this.statusTarget.textContent = this.savedValue
    return true
  }

  warn(event) {
    if (this.dirty || this.pending || this.submitting) { event.preventDefault(); event.returnValue = "" }
  }

  async navigate(event) {
    if (this.navigating || this.submitting || (!this.dirty && !this.pending)) return
    event.preventDefault()
    if (!await this.save()) return
    this.navigating = true
    window.Turbo.visit(event.detail.url)
  }

  async flush(event) {
    event.preventDefault()
    if (this.submitting) return
    this.submitting = true
    const submitter = event.submitter
    if (submitter) submitter.disabled = true
    this.dirty = true
    try {
      if (!await this.save()) return
      const data = new FormData(this.element)
      if (submitter?.name) data.set(submitter.name, submitter.value)
      const result = await this.persist(data)
      this.navigating = true
      window.Turbo.visit(result.location)
    } catch (error) {
      this.dirty = true
      this.statusTarget.textContent = error.message || this.errorValue
    } finally {
      this.submitting = false
      if (submitter) submitter.disabled = false
    }
  }
}
